import { removeAuth0Prefix } from '../../../users/removeAuth0Prefix/removeAuth0Prefix'
import { formatName } from '../../util/format-name'
import type { OwnerNameUser } from './get-owner-display-name'

/**
 * Minimal shape of `activity.details.message.from` — the message sender snapshot. Unlike the `Owner`
 * snapshot this carries a single pre-formatted `name` string and keys its id as `userId`, which is
 * stored auth0-prefixed (see `sendMessage`).
 */
export interface MessageSenderSnapshot {
  userId?: string
  name?: string
  isCustomer?: boolean
}

/**
 * Resolves the display name for a chat message sender, preferring the *live* user record over the
 * name frozen onto the activity when the message was sent.
 *
 * Same problem as `getOwnerDisplayName`, different shape: `sendMessage` formats the sender's name
 * once and stores the string, so a seat rename leaves every past message rendering the previous
 * person's name (#21800). Reading through the users map makes the display immune to that.
 *
 * Two cases keep the stored snapshot:
 * - Customer senders. `from.name` is the contact's name, not a user's, so there is nothing to
 *   resolve.
 * - Senders absent from the users map. Deactivated and removed users are not returned by
 *   `searchUsers`, and their historical authorship is deliberately preserved (see `deactivateUser`).
 */
export function getMessageSenderDisplayName({
  from,
  users
}: {
  from?: MessageSenderSnapshot | null
  users?: Record<string, OwnerNameUser> | null
}): string {
  if (!from) {
    return ''
  }

  if (from.isCustomer) {
    return from.name ?? ''
  }

  const user = from.userId ? users?.[removeAuth0Prefix(from.userId)] : undefined

  return formatName(user?.user_metadata) || from.name || ''
}
